import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { platformApi } from '../../api'
import type { Form } from '../../types'
import { IconPicker } from '../IconPicker'

/** How the form appears on the front page. These apply immediately; they are not part of a version. */
export default function DetailsTab({ form, onSaved }: { form: Form; onSaved: (f: Form) => void }) {
    const [name, setName] = useState(form.name)
    const [category, setCategory] = useState(form.category)
    const [description, setDescription] = useState(form.description)
    const [icon, setIcon] = useState(form.icon)
    const [busy, setBusy] = useState(false)

    const dirty = name !== form.name || category !== form.category || description !== form.description || icon !== form.icon
    const save = async () => {
        setBusy(true)
        try {
            const f = await platformApi.updateForm(form.id, { name: name.trim(), category: category.trim(), description: description.trim(), icon })
            onSaved(f)
            toast.success('Form details saved')
        } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Could not save')
        } finally {
            setBusy(false)
        }
    }

    return (
        <Card className="max-w-3xl">
            <CardHeader>
                <CardTitle>Details</CardTitle>
                <CardDescription>How this form appears on the front page. Changes apply immediately and don't need a new version.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-6">
                <FieldGroup>
                    <Field>
                        <FieldLabel htmlFor="fd-name">Name</FieldLabel>
                        <Input id="fd-name" value={name} onChange={(e) => setName(e.target.value)} />
                    </Field>
                    <Field>
                        <FieldLabel htmlFor="fd-cat">Category</FieldLabel>
                        <Input id="fd-cat" value={category} onChange={(e) => setCategory(e.target.value)} />
                        <FieldDescription>Forms with the same category are grouped together.</FieldDescription>
                    </Field>
                    <Field>
                        <FieldLabel htmlFor="fd-desc">Description</FieldLabel>
                        <Textarea id="fd-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
                        <FieldDescription>One or two sentences telling people when to use this form.</FieldDescription>
                    </Field>
                    <Field>
                        <FieldLabel>Icon</FieldLabel>
                        <IconPicker value={icon} onChange={setIcon} form={{ name, slug: form.slug, category }} />
                        <FieldDescription>Automatic picks one from the form's name.</FieldDescription>
                    </Field>
                </FieldGroup>
                <div>
                    <Button onClick={() => void save()} disabled={!dirty || busy || name.trim() === ''}>
                        {busy && <Spinner data-icon="inline-start" />}Save details
                    </Button>
                </div>
            </CardContent>
        </Card>
    )
}
